import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import vue from 'vue';
import { RouteSignatureModel, RouteSuggestionModel, SuggestionContentModel, SuggestionsModel } from '@/models/marker/reroute';
import { contentAPI } from '@/providers/apis/content/content';
import { searchAPI } from '@/providers/apis/search/search';
import MCIcon from '@/components/icons/mc-icon/mc-icon';

@Component({
    name: 'reroute-suggestion',
    components: {
      'mc-icon': MCIcon,
    },
})

export default class RerouteSuggestion extends Vue {

  private get routeRowElement() {
    const routeContents = this.routeContents;
    let totalElement = 0;
    for (let i = 0; i < routeContents.length; i++) {
      if (i % 3 === 0) {
        totalElement++;
      }
    }
    return new Array(totalElement).fill({});
  }

  // ----------------------------------------------------
  // Dependencies

  // ----------------------------------------------------
  // Properties

  @Prop()
  private lessonSuggestions!: RouteSuggestionModel;

  @Prop()
  private suggestedItems!: SuggestionsModel[];

  @Prop()
  private currentRoute!: boolean;

  @Prop()
  private activeLesson: any;

  private routeContents: any = [];

  private lineGroups: any = null;

  private isZoomIn = true;

  private resizeWidth = screen.width;

  private totalBoxItem: number = 3;

  private isLoading: boolean = false;

  public windowResizerChart() {
    this.loadData();
  }

  @Watch('suggestedItems')
  private onSuggestionItem() {
    this.routeContents = this.suggestedItems.filter((item) => item.isActive);
  }

  private get hasSuggestion() {
    return !!this.suggestedItems.filter((item) => !item.isActive).length;
  }

  // ----------------------------------------------------
  // Hooks

  private mounted() {
    this.isLoading = true;
    this.onSuggestionItem();
    this.$nextTick().then(() => {
      setTimeout(() => {
        this.loadData();
        document.addEventListener('resize', () => {
            this.resizeWidth = screen.width;
            this.windowResizerChart();
        });
      }, 500);
    });
  }



  // ----------------------------------------------------
  // Actions

  private onToggleZoom() {
    this.isZoomIn = !this.isZoomIn;
    setTimeout(() => this.loadData(), 50);
  }

  private onSelectLesson(content: any) {
    // this.loadLessonCollections(content);
  }

  private onShowMetadata(content: SuggestionsModel) {
    this.fetchAssessmentDetails(content);
  }

  private onShowSuggestion() {
    this.$emit('onShowSuggestion');
  }

  // ----------------------------------------------------
  // Methods

  private loadData() {
    this.drawChartLine();
    this.shuffleElementPosition();
  }

  // private loadSuggestionContent() {
  //   const lessonSuggestions: any = Object.assign({}, this.lessonSuggestions);
  //   let routeContents: RouteSignatureModel[] = [];
  //   const suggestionContent = Object.keys(lessonSuggestions);
  //   suggestionContent.forEach((suggestion) => {
  //       const suggestedItems = lessonSuggestions[suggestion];
  //       routeContents = [...routeContents, ...suggestedItems];
  //   });
  //   this.routeContents = routeContents;
  // }

  private drawChartLine() {
    const container = d3.select(this.$el).select('.reroute-line-chart-body .reroute-chart');
    container.select('svg').remove();
    const svg = container.append('svg');
    const clientRect = this.$el.getClientRects()[0];
    const width = clientRect.width;
    const curveStartX = width * 0.3;
    svg.attr('width', width).attr('height', clientRect.height);
    this.lineGroups = svg.append('g');
  }

  private shuffleElementPosition() {
    const chartBody = this.$el.querySelector('.reroute-line-chart-body');
    const rowItems = chartBody!.querySelectorAll('.row-content-panel');
    const lineGroups = this.lineGroups;
    let startPointX = 20;
    let startPointY = 0;
    let isRightBend = true;

    Array.from(rowItems).forEach((rowItem, rowIndex) => {
      let rowContents: any = rowItem.querySelectorAll('.collection-panel-sections');
      rowContents = Array.from(rowContents);
      if (rowContents) {
        rowContents =
          rowIndex % 2 === 0 ? rowContents : rowContents.reverse();
        rowContents.forEach((el: any, elIndex: number) => {
          el = el.querySelector('.collection-icon');
          const midHeight = el.offsetHeight / 2;
          // const midWidth = el.offsetWidth / 2;
          let x = el.offsetLeft + el.offsetWidth;
          let y = el.offsetTop + midHeight;
          let curveX1 = startPointX;
          let curveY1 = y;
          let curveX2 = x;
          let curveY2 = y;

          if (x === startPointX) {
            const arcPoint = y - startPointY;
            curveX1 = isRightBend
              ? startPointX + arcPoint
              : startPointX - arcPoint;
            curveX2 = isRightBend ? x + arcPoint : x - arcPoint;
            curveY1 = startPointY;
            isRightBend = !isRightBend;
          }
          lineGroups
            .append('path')
            .attr(
              'd',
              `M ${startPointX} ${startPointY} C ${curveX1} ${curveY1} ${curveX2} ${curveY2} ${x} ${y}`,
            );
          startPointX = x;
          startPointY = y;
          if (
            rowItems.length - 1 === rowIndex &&
            rowContents.length - 1 === elIndex
          ) {
            if (rowIndex % 2 === 0) {
              y = startPointY + 50;
              curveX1 = startPointX + 50;
              curveY1 = startPointY;
              curveX2 = curveX1;
              curveY2 = y;

              lineGroups
                .append('path')
                .attr(
                  'd',
                  `M ${startPointX} ${startPointY} C ${curveX1} ${curveY1} ${curveX2} ${curveY2} ${x} ${y}`,
                );
            }
            // End curve to join the next lesson
            startPointY = y;
            x = 20;
            y = this.$el.clientHeight;
            curveX1 = x;
            curveY1 = startPointY;
            curveX2 = x;
            curveY2 = startPointY;
            lineGroups
              .append('path')
              .attr(
                'd',
                `M ${startPointX} ${startPointY} C ${curveX1} ${curveY1} ${curveX2} ${curveY2} ${x} ${y}`,
              );
          }
        });
      }
    });
  }


  private fetchAssessmentDetails(content: SuggestionsModel) {
    const isResourceContent = content.format.indexOf('resource') !== -1;
    const format = isResourceContent ? content.format.split('-')[1] : content.format;
    const resourceData: SuggestionContentModel[] | any = content.contents;
    if (isResourceContent) {
          const resource: SuggestionContentModel = resourceData[0] || {};
          const params = {
            q: resource.originalContentId,
          };
          const searchContent =  resource.format === 'resource'
            ? searchAPI.fetchResources(params) : searchAPI.fetchQuestions(params);
          searchContent.then((assessmentData) => {
            this.$emit('onShowMetadata', assessmentData.searchResults[0]);
          });
        } else {
          const params: any = {
            q: content.id,
          };
          if (content.format === 'lesson') {
            searchAPI.fetchLessons(params).then((lessonData) => {
              this.$emit('onShowMetadata', lessonData[0]);
            });
          } else {
            params['flt.collectionType'] = content.format,
            searchAPI.fetchCollections(params).then((assessmentData) => {
              this.$emit('onShowMetadata', assessmentData.searchResults[0]);
            });
          }
        }
  }

}
